import React, { useEffect, useMemo, useState } from 'react';
import { alpha, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { GREEN, MONO } from './scanUi';
import { DEFAULT_BASE, QUESTIONS, calculate, explain, parseVector, toVector } from './cvssLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const RATING = { None: '#94a3b8', Low: '#22c55e', Medium: '#f59e0b', High: '#f97316', Critical: '#ef4444' };
const START = { ...DEFAULT_BASE, C: 'H', I: 'H', A: 'H' };

function Choice({ on, onClick, children }) {
  return (
    <Button onClick={onClick} sx={{ textTransform: 'none', fontWeight: 600, px: 2, py: 0.9, borderRadius: 2.5, border: 1, borderColor: on ? GREEN : 'divider', color: on ? GREEN : 'text.secondary', bgcolor: on ? alpha(GREEN, 0.12) : 'transparent' }}>{children}</Button>
  );
}

export default function CvssCalculatorPage() {
  const [base, setBase] = useState(START);
  const [text, setText] = useState(toVector(START));
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const vector = useMemo(() => toVector(base), [base]);
  const result = useMemo(() => calculate(base), [base]);
  const color = RATING[result.rating];

  // The paste box follows the answers, unless the person is in the middle of typing a vector that is not valid yet.
  useEffect(() => { if (!error) setText(vector); }, [vector]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = (key, value) => { setError(''); setBase((b) => ({ ...b, [key]: value })); };
  const paste = (value) => {
    setText(value);
    if (!value.trim()) { setError(''); return; }
    const parsed = parseVector(value);
    if (parsed.error) { setError(parsed.error); return; }
    setError(''); setBase(parsed.base);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(vector); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { window.prompt('Copy the vector:', vector); }
  };
  const groups = [...new Set(QUESTIONS.map((q) => q.group))];

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>CVSS calculator</Typography>
        <Typography color="text.secondary" variant="body2">Answer a few plain questions, or paste a vector from a report, and get the score</Typography>
      </Box>

      <Box sx={{ ...panel, p: 2.6 }}>
        <Typography variant="overline" color="text.secondary">Have a vector? Paste it here</Typography>
        <TextField fullWidth size="small" value={text} onChange={(e) => paste(e.target.value)} error={Boolean(error)} helperText={error || 'For example CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H. The answers below follow it.'}
          inputProps={{ spellCheck: false }} sx={{ mt: 0.5, '& input': { fontFamily: MONO, fontSize: 13.5 } }} />
      </Box>

      <Stack direction="row" gap={2.5} flexWrap="wrap" alignItems="flex-start">
        <Box sx={{ ...panel, flex: '2 1 560px', overflow: 'hidden' }}>
          {groups.map((g, gi) => (
            <Box key={g}>
              <Typography sx={{ p: 2.2, fontWeight: 700, borderTop: gi ? 1 : 0, borderColor: 'divider' }}>{g}</Typography>
              {QUESTIONS.filter((q) => q.group === g).map((q) => (
                <Box key={q.key} sx={{ px: 2.8, py: 1.8, borderTop: 1, borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 600 }}>{q.q}</Typography>
                  <Typography variant="caption" color="text.secondary">{q.hint}</Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {q.options.map(([value, label]) => <Choice key={value} on={base[q.key] === value} onClick={() => answer(q.key, value)}>{label}</Choice>)}
                  </Stack>
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <Stack spacing={2} sx={{ flex: '1 1 320px', position: { lg: 'sticky' }, top: { lg: 16 } }}>
          <Box sx={{ ...panel, p: 3, borderColor: alpha(color, 0.45), background: `linear-gradient(135deg, ${alpha(color, 0.12)}, transparent 65%)`, display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ width: 150, height: 150, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `conic-gradient(${color} ${result.base * 10}%, ${alpha('#64748b', 0.25)} 0)` }}>
              <Box sx={{ width: 118, height: 118, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 40, fontWeight: 800, fontFamily: MONO, color, lineHeight: 1 }}>{result.base.toFixed(1)}</Typography>
                <Typography variant="caption" color="text.secondary">{result.rating}</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Base score</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, mt: 0.5 }}>{explain(base)}</Typography>
            </Box>
          </Box>
          <Box sx={{ ...panel, p: 2.2 }}>
            <Typography variant="overline" color="text.secondary">Vector</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: GREEN, wordBreak: 'break-all', bgcolor: alpha(GREEN, 0.06), p: 1.3, borderRadius: 2, mt: 0.5 }}>{vector}</Typography>
            <Stack direction="row" gap={1} sx={{ mt: 1.2 }}>
              <Button size="small" variant="outlined" color="inherit" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
              <Button size="small" variant="outlined" color="inherit" onClick={() => { setError(''); setBase(START); }}>Reset</Button>
            </Stack>
          </Box>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">CVSS 3.1 gives a problem a score from 0 to 10: 0.1 to 3.9 low, 4.0 to 6.9 medium, 7.0 to 8.9 high, 9.0 and above critical. Scores follow the official FIRST formula. Version 4.0 vectors are not supported yet.</Typography>
    </Stack>
  );
}
